/** Card ids are prefixed so no queue request or transfer id can ever match a team offer. */
const CARD_PREFIX = 'team:';
/** The server's offer window (vrs-server/ws/interpreter-teams.js TEAM_OFFER_MS). */
export const TEAM_OFFER_WINDOW_MS = 60000;
export function teamCardId(teamId) {
    return `${CARD_PREFIX}${teamId}`;
}
function text(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}
/** The offer card for a `team_offer`, or null for a malformed one. */
export function teamOfferToRequest(payload, now = Date.now()) {
    const teamId = text(payload?.teamId);
    if (!teamId) {
        return null;
    }
    return {
        callType: payload.callType === 'vri' ? 'vri' : 'vrs',
        clientName: text(payload.clientName) || 'Client',
        id: teamCardId(teamId),
        language: text(payload.language) || '',
        offerExpiresAt: text(payload.expiresAt),
        roomName: text(payload.roomName),
        team: {
            callId: text(payload.callId),
            fromInterpreterName: text(payload.fromInterpreterName),
            teamId
        },
        timestamp: now
    };
}
/** Who is asking: "From Sam". */
export function teamOfferLine(team) {
    return `From ${team.fromInterpreterName || 'a colleague'}`;
}
/**
 * What Home says when `team_offer_ended` takes a card down, or null when
 * this interpreter caused it (their decline, their answer on another device).
 */
export function teamOfferEndedText(reason) {
    switch (reason) {
        case 'withdrawn': return 'Team request withdrawn.';
        case 'timeout': return 'Team request expired.';
        case 'taken': return 'Taken by another interpreter.';
        case 'transferred': return 'Call transferred.';
        case 'call_ended':
        case 'primary_left': return 'Call ended.';
        default: return null;
    }
}
/** The match a `team_joined` opens: the call's room, with this interpreter as its teammate. */
export function teamJoinedMatch(data) {
    return {
        callId: data.callId,
        callType: data.callType === 'vri' ? 'vri' : 'vrs',
        clientId: '',
        clientName: text(data.clientName) || 'Client',
        interpreterId: text(data.primaryInterpreterId) || '',
        interpreterName: text(data.primaryInterpreterName) || 'Interpreter',
        language: 'ASL',
        requestId: data.callId,
        roomName: text(data.roomName) || '',
        team: { primaryInterpreterName: text(data.primaryInterpreterName), teamId: data.teamId }
    };
}
/** The tools' starting state: the teammate of a joined team, or nobody yet. */
export function initialCallTeam(team) {
    return team?.teamId ? { phase: 'teammate', primaryInterpreterName: team.primaryInterpreterName, teamId: team.teamId }
        : { phase: 'idle' };
}
const REQUEST_ENDED_NOTES = Object.freeze({
    cancelled: '',
    declined: 'Declined.',
    timeout: 'No one answered.'
});
/** To the primary; releasing the teammate was their own action, so it says nothing. */
const ENDED_NOTES = Object.freeze({
    left: 'Teammate left.',
    transferred: 'Team ended.'
});
/** Refusals of team_request, in the tools' words. */
export const TEAM_REFUSAL_NOTES = Object.freeze({
    TEAM_NO_CANDIDATES: 'No interpreter is available.',
    TEAM_TARGET_UNAVAILABLE: 'That interpreter is not available.',
    TARGET_OFFLINE: 'That interpreter is not available.',
    TEAM_ALREADY_OPEN: 'A team request is already open.',
    TEAM_NOT_ALLOWED: 'Not available on this call.',
    SERVICE_REGION_NOT_COVERED: 'Not available on this call.',
    TRANSFER_TARGET_LANGUAGE_MISMATCH: 'Not available on this call.',
    TRANSFER_TARGET_SERVICE_MODE_MISMATCH: 'Not available on this call.',
    TRANSFER_TARGET_DEMO_ONLY: 'Not available on this call.',
    TRANSFER_TARGET_NOT_FOUND: 'Not available on this call.'
});
/** One call's team, as its tools see it. Events for another team are ignored. */
export function reduceCallTeam(state, event) {
    const sameTeam = (data) => 'teamId' in state && state.teamId === data.teamId;
    switch (event.type) {
        case 'request':
            return state.phase === 'idle' || state.phase === 'left'
                ? { phase: 'requesting', toInterpreterId: event.toInterpreterId } : state;
        case 'pending':
            return state.phase === 'requesting'
                ? { phase: 'pending', teamId: event.data.teamId, toInterpreterId: state.toInterpreterId } : state;
        case 'accepted':
            return state.phase === 'pending' && sameTeam(event.data)
                ? { phase: 'teamed', teamId: event.data.teamId, teammateName: text(event.data.teammateName) } : state;
        case 'requestEnded':
            return (state.phase === 'pending' || state.phase === 'requesting') && (state.phase === 'requesting' || sameTeam(event.data))
                ? { phase: 'idle', note: REQUEST_ENDED_NOTES[event.data.reason || ''] || undefined } : state;
        case 'ended':
            if (!sameTeam(event.data))
                return state;
            return state.phase === 'teammate'
                ? { phase: 'left', note: event.data.reason === 'released' ? 'Released from the team.' : undefined }
                : { phase: 'idle', note: ENDED_NOTES[event.data.reason || ''] || undefined };
        case 'refused':
            return state.phase === 'requesting'
                ? { phase: 'idle', note: TEAM_REFUSAL_NOTES[event.code || ''] || 'Team request failed.' } : state;
        case 'joined':
            return initialCallTeam(event.team);
        default:
            return state;
    }
}
/** The line the tools show for the team, or '' when there is nothing to say. */
export function callTeamLine(state) {
    switch (state.phase) {
        case 'requesting': return 'Asking…';
        case 'pending': return 'Waiting for a teammate…';
        case 'teamed': return state.teammateName ? `Teaming with ${state.teammateName}` : 'Teammate joined';
        case 'teammate': return state.primaryInterpreterName ? `Teaming with ${state.primaryInterpreterName}` : 'On the team';
        default: return state.note || '';
    }
}
